import { useMemo } from "react"
import {
  Avatar,
  Box,
  Card,
  CardProps,
  Divider,
  Typography,
  useTheme,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import BookIcon from "@mui/icons-material/Book"

import { getIcon } from "utils/icon"
import { useTranslation } from "providers/i18n"
import { QRCode } from "components/QRCode"
import { Image } from "components/Image"

export interface IQrCodeData {
  type: "ios" | "android" | "link"
  value?: string
  labelImage?: string
}

export interface IReservationCardData {
  logo?: string
  primaryText?: string
  secondaryText?: string
  qrCodes: IQrCodeData[]
}

export interface IReservationCardProps extends CardProps {
  data: IReservationCardData
}

export const ReservationQRCodeCard: React.FC<IReservationCardProps> = ({
  sx = {},
  data,
  ...props
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const {
    root,
    logoContainer,
    logo: logoStyle,
    fallbackLogoIcon,
    divider,
    textContainer,
    primaryText: primaryTextStyle,
    secondaryText: secondaryTextStyle,
    labelImage: labelImageStyle,
    content,
    qrCodeContainer,
    qrCodeListItem,
    qrCode,
  } = theme.components[`ReservationQRCodeCard`]?.styleOverrides || {}

  const {
    logo,
    primaryText = "Scan here for",
    secondaryText = "Reservation",
    qrCodes,
  } = data || {}

  const fallbackIcon = useMemo(
    () =>
      getIcon(
        theme?.assets?.reservationQRcodeCard?.fallbackIcon,
        fallbackLogoIcon,
        {
          iconCompProps: {
            className: "ReservationQRCodeCard-fallbackLogoIcon",
          },
        }
      ) || (
        <BookIcon
          sx={fallbackLogoIcon}
          className="ReservationQRCodeCard-fallbackLogoIcon"
        />
      ),
    [theme, fallbackLogoIcon]
  )

  return (
    <Card sx={deepmerge(root, sx)} {...props}>
      <Box sx={logoContainer}>
        <Avatar sx={logoStyle} src={logo}>
          {fallbackIcon}
        </Avatar>
      </Box>
      <Divider sx={divider} />
      <Box sx={content}>
        <Box sx={qrCodeContainer}>
          {qrCodes.map(({ value, labelImage }, index) => {
            return value ? (
              <Box sx={qrCodeListItem} key={`${value}-${index}`}>
                <QRCode sx={qrCode} url={value} />
                {labelImage && <Image sx={labelImageStyle} src={labelImage} />}
              </Box>
            ) : null
          })}
        </Box>
      </Box>
      <Box sx={textContainer}>
        <Typography sx={primaryTextStyle}>{t(primaryText)}</Typography>
        <Typography sx={secondaryTextStyle}>{t(secondaryText)}</Typography>
      </Box>
    </Card>
  )
}
